import { useState } from 'react'
import { Box, Typography, Link, Chip, Tooltip } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import dayjs from 'dayjs'
import { useNotification, useRouteQuery, useTranslator } from '@/hooks'
import { IBalanceTransactionRecord, ICompany } from '@/@data'
import { BalanceDetailFilterPast } from './BalanceDetailFilterPastTable'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'

interface BalanceTableProps {
  data: IBalanceTransactionRecord[]
  meta: unknown
  onRowClick?: (record: IBalanceTransactionRecord) => void
  checkHideDifferent: boolean
}

export const BalancePastDetailTable = ({ data, meta, checkHideDifferent }: BalanceTableProps) => {
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()
  const n = useNotification()

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const monthParam = searchQuery.get('month') || dayjs().startOf('month').format('YYYY-MM-DD')

  const COLUMN_KEYS = {
    CREATE_DATE: 'create_date',
    CREATOR: 'creator',
    EMPLOYER: 'employer',
    DATE_INPUT_PAY: 'date_input_pay',
    PAY_DIFFERENT: 'pay_different',
    VOUCHER: 'voucher',
    VOUCHER_TAX: 'voucher_tax',
    RETURN_WAGES_DISBURSEMENT: 'return_wages_disbursement',
    STATUS_TRANSACTION: 'status_transaction',
    NOTE: 'note',
    FILE: 'file'
  }

  const columnMap = [
    { key: COLUMN_KEYS.CREATE_DATE, label: __('Create Date') },
    { key: COLUMN_KEYS.CREATOR, label: __('Creator') },
    { key: COLUMN_KEYS.EMPLOYER, label: __('Employer') },
    { key: COLUMN_KEYS.DATE_INPUT_PAY, label: __('Date Input Pay') },
    { key: COLUMN_KEYS.PAY_DIFFERENT, label: __('Pay Different') },
    { key: COLUMN_KEYS.VOUCHER, label: __('Voucher') },
    { key: COLUMN_KEYS.VOUCHER_TAX, label: __('Voucher Tax') },
    { key: COLUMN_KEYS.RETURN_WAGES_DISBURSEMENT, label: __('Return Wages Disbursement') },
    { key: COLUMN_KEYS.STATUS_TRANSACTION, label: __('Status Transaction') },
    { key: COLUMN_KEYS.NOTE, label: __('Note') },
    { key: COLUMN_KEYS.FILE, label: __('File') }
  ]

  const [visibleColumns, setVisibleColumns] = useState<string[]>(columnMap.map(c => c.key))
  const isColumnVisible = (key: string) => visibleColumns.includes(key)

  return (
    <>
      <BalanceDetailFilterPast
        visibleColumns={visibleColumns}
        columns={columnMap}
        onSelectColumns={cols => {
          setVisibleColumns(cols)
        }}
      />

      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e0e0e0' }}>
          <thead>
            <tr className='border-right-custom'>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  width: '200px',
                  minWidth: '200px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Balance Transaction')}
              </th>

              {isColumnVisible(COLUMN_KEYS.CREATE_DATE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Create Date')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.CREATOR) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Creator')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.EMPLOYER) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    width: '200px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Employer')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.DATE_INPUT_PAY) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Date Input Pay')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.PAY_DIFFERENT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Pay Different')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Voucher')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.VOUCHER_TAX) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Voucher Tax')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.RETURN_WAGES_DISBURSEMENT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Return Wages Disbursement')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.STATUS_TRANSACTION) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Status Transaction')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.NOTE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Note')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.FILE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('File')}
                </th>
              )}
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, index) => {
                const status = (row as any).status || 'pending'

                const statusConfig = {
                  pending: { label: 'Pending', color: '#FF9F43', bgColor: '#FFF3E0' },
                  approved: { label: 'Approved', color: '#00C851', bgColor: '#E8F5E8' },
                  rejected: { label: 'Rejected', color: '#FF4444', bgColor: '#FFEBEE' },
                  deleted: { label: 'Deleted', color: '#FF4444', bgColor: '#FFEBEE' }
                } as const

                return (
                  <tr key={index}>
                    <td
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0'
                      }}
                    >
                      <div
                        onClick={() =>
                          navigate(`/cms/finance/balance/${row.company_id}/transaction/${row.id}?month=${monthParam}`)
                        }
                        className='text-[#3551F2] hover:underline no-underline cursor-pointer'
                      >
                        {`${row?.code}`}
                      </div>
                    </td>

                    {isColumnVisible(COLUMN_KEYS.CREATE_DATE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {dayjs(row?.created_at).format('DD/MM/YYYY')}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.CREATOR) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div className='flex items-center gap-2'>
                          {getAvatar({
                            logo_file: row?.created_user?.avatar_file,
                            name: row.created_user?.first_name || '-'
                          })}
                          <div className='flex flex-col items-left'>
                            <Typography
                              className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                              color='text.primary'
                            >
                              {row.created_user?.first_name || '-'}
                            </Typography>
                            <Typography variant='body2'>{row.created_user?.email || '-'}</Typography>
                          </div>
                        </div>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.EMPLOYER) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Link href={`/cms/employer/${row?.company_id}`} className='flex items-center gap-4 no-underline'>
                          {getAvatar({ logo_file: row.company?.logo_file, name: row.company?.name || '-' })}
                          <div className='flex flex-col items-left'>
                            <Typography
                              className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                              color='text.primary'
                            >
                              {row.company?.name || '-'}
                            </Typography>
                            <Typography variant='body2'>{row.company?.system_code || '-'}</Typography>
                          </div>
                        </Link>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.DATE_INPUT_PAY) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {row?.approve_at ? dayjs((row as any).approve_at).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.PAY_DIFFERENT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        {checkHideDifferent ? (
                          '-'
                        ) : (
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {__n(row?.different, 'đ')}
                          </Typography>
                        )}
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.voucher, 'đ')}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.VOUCHER_TAX) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.voucher_tax, 'đ')}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.RETURN_WAGES_DISBURSEMENT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n((row as any).debt_disbursement || 0, 'đ')}
                        </Typography>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.STATUS_TRANSACTION) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Chip
                          label={statusConfig[status as keyof typeof statusConfig]?.label || 'Unknown'}
                          size='small'
                          sx={{
                            backgroundColor: statusConfig[status as keyof typeof statusConfig]?.bgColor || '#F5F5F5',
                            color: statusConfig[status as keyof typeof statusConfig]?.color || '#666',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}
                        />
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.NOTE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div className='flex justify-center'>
                          {(row as any).note ? (
                            <div className='flex items-center bg-[#DEDCE0] rounded-full p-[6px]'>
                              <Tooltip children={<i className='tabler-note text-[18px]' />} title={row.note} />
                            </div>
                          ) : (
                            ''
                          )}
                        </div>
                      </td>
                    )}

                    {isColumnVisible(COLUMN_KEYS.FILE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div
                          className='flex items-center gap-2 cursor-pointer'
                          onClick={async () => {
                            const fileUrl = (row as any)?.file?.url

                            if (fileUrl) {
                              try {
                                const response = await fetch(fileUrl)
                                const blob = await response.blob()
                                const url = window.URL.createObjectURL(blob)
                                const link = document.createElement('a')

                                link.href = url
                                link.download = (row as any)?.file?.name || 'download'
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                                window.URL.revokeObjectURL(url)
                              } catch (error) {
                                n.error(__('Failed to download file'))
                              }
                            }
                          }}
                        >
                          <i className='tabler-paperclip text-[#3551F2] text-[18px]' />
                          <Typography
                            className='text-[#3551F2] text-[15px] leading-[22px] font-normal truncate max-w-[200px]'
                            title={(row as any)?.file?.name}
                          >
                            {(row as any)?.file?.name || 'Filename.Etc'}
                          </Typography>
                        </div>
                      </td>
                    )}

                    <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                      <div className='flex justify-center'>
                        <div
                          onClick={() =>
                            navigate(`/cms/finance/balance/${row.company_id}/transaction/${row.id}?month=${monthParam}`)
                          }
                          className='text-[#3551F2] hover:underline no-underline'
                        >
                          <i className='tabler-edit text-[#2F2B3DB2] text-[18px] cursor-pointer hover:text-[#3551F2]' />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  style={{ padding: '24px 16px', textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}
                  colSpan={2 + visibleColumns.length}
                  rowSpan={2 + visibleColumns.length}
                >
                  <Typography className='text-[15px] leading-[22px] font-normal text-gray-500'>
                    {__('No Data')}
                  </Typography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {meta && (
        <TablePagination
          meta={
            {
              ...(meta as any),
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
